/**
 * CAOS-306 — the app list-view data source.
 *
 * Opening an app lands on its landing tab's LIST VIEW (CAOS-302). This module is the thin,
 * same-origin read that turns a tab's target object into a real list: the object's fields become the
 * COLUMNS (describe) and the org's records become the ROWS (query), exactly like the original Dragon
 * OS list views — not a placeholder "No records to display." line.
 *
 * Three faithful details the naive projection skips:
 *   • Lookups show NAMES, not ids. The Data API serializes a reference field as the raw target id
 *     (`record-serialization.ts` passes references through untouched); a list that showed the id would
 *     be unreadable. So for every reference column we batch-resolve the referenced object's records
 *     once and substitute each id for its name-field value.
 *   • Columns are the object's REAL fields — the name field first, then the authored (non-system)
 *     fields — so the header row reads like the object, capped so a wide object stays legible.
 *   • The empty state is COLUMNS-PRESENT: when the object genuinely has no rows the caller still
 *     renders the header row (this module reports `empty: true` with the columns intact), never a
 *     bare "nothing here" with no shape.
 *
 * Same-origin and fail-soft: every read goes through the host `/api/*` proxy (which injects the
 * session bearer from the cookie), and any transport/parse failure degrades to an honest `error`
 * string rather than throwing into the render path. Pure of the DOM — it returns plain data so the
 * shell-frame renderer owns all element creation (and the unit tests drive it with a fetch stub).
 */
import { formatPlace } from '@caos/ui/place-format';

/** A minimal response shape (so a test stub need not build a whole `Response`). */
export interface DataFetchResponse {
  json(): Promise<unknown>;
}

/** The fetch seam: same-origin GET/POST against the host `/api/*` proxy. */
export type DataFetch = (path: string, init?: { method?: string }) => Promise<DataFetchResponse>;

/** Default seam — a same-origin, never-cached JSON read behind the host BFF proxy. */
export const defaultDataFetch: DataFetch = (path, init) =>
  fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
    ...(init ?? {}),
  }) as unknown as Promise<DataFetchResponse>;

let dataFetch: DataFetch = defaultDataFetch;

/** Override the data fetch seam (production wiring / tests). */
export function setDataFetch(fn: DataFetch): void {
  dataFetch = fn;
}

/** The most columns a list view renders before it stops (keeps a wide object legible). */
const MAX_COLUMNS = 8;
/** The page size for the roster + for each reference-name resolution sweep. */
const PAGE_LIMIT = 50;
const REFERENCE_RESOLVE_LIMIT = 200;

/** A localizable label as the Data API emits it (`{ value }`) — or a bare string defensively. */
type WireLabel = string | { value?: string } | undefined;

/** One field of the object, from `data/describe`. */
interface DescribeField {
  apiName: string;
  dataType?: string;
  provenance?: 'standard' | 'custom' | 'system';
  referenceTo?: string | null;
  required?: boolean;
  label?: WireLabel;
}

/** One object's shape facts, from `data/describe-object`. */
interface ObjectDescribe {
  apiName: string;
  nameField?: string;
  label?: WireLabel;
  pluralLabel?: WireLabel;
}

/** A projected list column. */
export interface ListColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
}

/** A projected list row: id + a resolved display string per column key. */
export interface ListRow {
  id: string;
  cells: Record<string, string>;
}

/** The projected list view the renderer consumes. */
export interface ListView {
  object: string;
  singularLabel: string;
  pluralLabel: string;
  columns: ListColumn[];
  rows: ListRow[];
  /** True when the object has columns but genuinely zero rows (columns-present empty state). */
  empty: boolean;
  /** Present only on a read failure — an honest, human line the renderer shows instead of a table. */
  error?: string;
}

/**
 * The bare field key for a (possibly object-qualified) name-field reference. `data/describe-object`
 * reports `nameField` qualified (`"account.name"`), while a field's `apiName` is bare (`"name"`); the
 * bare key is the segment after the last dot, so the two match.
 */
function bareFieldKey(nameField: string | undefined): string | undefined {
  if (!nameField) return undefined;
  const parts = nameField.split('.');
  return parts[parts.length - 1] || undefined;
}

/** Read a wire label to its string value (or a fallback). */
function labelText(label: WireLabel, fallback: string): string {
  if (typeof label === 'string' && label.length > 0) return label;
  if (label && typeof label === 'object' && typeof label.value === 'string' && label.value.length > 0) {
    return label.value;
  }
  return fallback;
}

/** Unwrap the substrate envelope (`{ ok, data }`) to its data, or null on a non-success. */
function unwrap(envelope: unknown): Record<string, unknown> | null {
  if (envelope && typeof envelope === 'object' && (envelope as { ok?: unknown }).ok === true) {
    const data = (envelope as { data?: unknown }).data;
    if (data && typeof data === 'object') return data as Record<string, unknown>;
  }
  return null;
}

/** GET a same-origin JSON route and unwrap it; null on any failure (fail-soft). */
async function getData(path: string): Promise<Record<string, unknown> | null> {
  try {
    const res = await dataFetch(path);
    return unwrap(await res.json());
  } catch {
    return null;
  }
}

/** Resolve a canonical serialized value to a readable display string (no ids, no `[object Object]`). */
function displayValue(value: unknown, resolveReference: (id: string) => string | undefined): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    // A reference field serializes as the raw target id — swap it for the resolved name when we have one.
    const name = resolveReference(value);
    return name ?? value;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // Currency money `{ amount, currency }`.
    if (typeof obj['amount'] === 'string') {
      const currency = typeof obj['currency'] === 'string' ? ` ${obj['currency']}` : '';
      return `${obj['amount'] as string}${currency}`;
    }
    // Zoned datetime `{ local, zone }`.
    if (typeof obj['local'] === 'string') {
      const zone = typeof obj['zone'] === 'string' ? ` (${obj['zone']})` : '';
      return `${obj['local'] as string}${zone}`;
    }
    // Geolocation `{ latitude, longitude }` (CAOS-642) — reached only when a record carries no display
    // string for it. Formatted by the renderer's own formatter, so a place never reads two ways.
    if (typeof obj['latitude'] === 'number' && typeof obj['longitude'] === 'number') {
      return formatPlace({ latitude: obj['latitude'], longitude: obj['longitude'] });
    }
  }
  return String(value);
}

/**
 * Load the list view for an object: its columns (describe) + rows (query), with reference columns
 * resolved to names. Fail-soft — a read failure returns a view carrying an `error` string.
 */
export async function loadListView(object: string): Promise<ListView> {
  const [describe, objectsPayload, queryPayload] = await Promise.all([
    getData(`/api/data/describe?object=${encodeURIComponent(object)}`),
    getData('/api/data/describe-object'),
    // CAOS-642 — `display=true` asks for the kernel's display block, the string a person reads in their
    // own locale, which the record page already shows (content-source.ts).
    getData(`/api/data/query?object=${encodeURIComponent(object)}&limit=${PAGE_LIMIT}&display=true`),
  ]);

  // Index every object's shape facts (name field + labels) for this object and its reference targets.
  const objectsRaw = Array.isArray(objectsPayload?.['objects']) ? (objectsPayload!['objects'] as ObjectDescribe[]) : [];
  const objectByKey = new Map<string, ObjectDescribe>();
  for (const o of objectsRaw) if (o?.apiName) objectByKey.set(o.apiName, o);
  const selfMeta = objectByKey.get(object);
  const singularLabel = labelText(selfMeta?.label, object);
  const pluralLabel = labelText(selfMeta?.pluralLabel, `${singularLabel} records`);

  if (!describe || !Array.isArray(describe['fields'])) {
    return { object, singularLabel, pluralLabel, columns: [], rows: [], empty: true, error: `Could not load the ${singularLabel} columns.` };
  }
  const fields = describe['fields'] as DescribeField[];
  const nameFieldKey = bareFieldKey(selfMeta?.nameField);

  // Column order: the name field first, then the authored (non-system) fields, capped for legibility.
  const ordered: DescribeField[] = [];
  const nameField = nameFieldKey ? fields.find((f) => f.apiName === nameFieldKey) : undefined;
  if (nameField) ordered.push(nameField);
  for (const f of fields) {
    if (f === nameField) continue;
    if (f.provenance === 'system') continue;
    ordered.push(f);
    if (ordered.length >= MAX_COLUMNS) break;
  }
  const columns: ListColumn[] = ordered.map((f) => ({ key: f.apiName, label: labelText(f.label, f.apiName) }));

  // CAOS-642 — the kernel writes its display block under the record key `display`, over any authored field
  // of that name (record-serialization.ts documents the tradeoff). For an object that has such a field the
  // block would hide the field's own value, so its rows are read again without the block and every cell is
  // formatted here. Only that object pays for the second read; every other list keeps one.
  const declaresDisplayField = fields.some((f) => f.apiName === 'display');
  const rowsPayload = declaresDisplayField
    ? await getData(`/api/data/query?object=${encodeURIComponent(object)}&limit=${PAGE_LIMIT}`)
    : queryPayload;
  const records = rowsPayload && Array.isArray(rowsPayload['records']) ? (rowsPayload['records'] as Record<string, unknown>[]) : [];

  // Resolve every reference column's ids → names, one sweep per referenced object.
  const referenceFields = ordered.filter((f) => typeof f.referenceTo === 'string' && f.referenceTo);
  const referenceNames = await resolveReferenceNames(referenceFields, records, objectByKey);
  const resolveReference = (id: string): string | undefined => referenceNames.get(id);

  const rows: ListRow[] = records.map((rec, i) => {
    const cells: Record<string, string> = {};
    // A cell shows the kernel's display string when there is one. A reference column has none — the
    // display block carries no string for a reference — so it still resolves to the target's name here.
    const display = rec['display'] && typeof rec['display'] === 'object' ? (rec['display'] as Record<string, unknown>) : {};
    for (const f of ordered) {
      const shown = display[f.apiName];
      cells[f.apiName] = typeof shown === 'string' && shown !== '' ? shown : displayValue(rec[f.apiName], resolveReference);
    }
    return { id: String(rec['id'] ?? i), cells };
  });

  return { object, singularLabel, pluralLabel, columns, rows, empty: rows.length === 0 };
}

/**
 * Batch-resolve reference ids → their target object's name-field value. One `data/query` per distinct
 * referenced object (never one per row), building a flat `id → name` map. Fail-soft: an unreadable
 * target simply leaves its ids unresolved (the renderer then shows the id, never a crash).
 */
async function resolveReferenceNames(
  referenceFields: DescribeField[],
  records: Record<string, unknown>[],
  objectByKey: Map<string, ObjectDescribe>,
): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  // Which referenced ids actually appear, grouped by target object.
  const idsByTarget = new Map<string, Set<string>>();
  for (const f of referenceFields) {
    const target = f.referenceTo as string;
    for (const rec of records) {
      const v = rec[f.apiName];
      if (typeof v === 'string' && v.length > 0) {
        (idsByTarget.get(target) ?? idsByTarget.set(target, new Set()).get(target)!).add(v);
      }
    }
  }

  await Promise.all(
    [...idsByTarget.entries()].map(async ([target, ids]) => {
      if (ids.size === 0) return;
      const nameField = bareFieldKey(objectByKey.get(target)?.nameField);
      if (!nameField) return;
      const payload = await getData(`/api/data/query?object=${encodeURIComponent(target)}&limit=${REFERENCE_RESOLVE_LIMIT}`);
      const targetRecords = payload && Array.isArray(payload['records']) ? (payload['records'] as Record<string, unknown>[]) : [];
      for (const rec of targetRecords) {
        const id = rec['id'];
        const name = rec[nameField];
        if (typeof id === 'string' && ids.has(id) && typeof name === 'string') names.set(id, name);
      }
    }),
  );
  return names;
}
